import React, { useState, useEffect } from 'react';
import { OrganizationalRole } from '../../types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';

interface RoleFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RoleFormData) => Promise<void>;
  editingRole: OrganizationalRole | null;
}

export interface RoleFormData {
  name: string;
  isAdmin: boolean;
  displayOrder: string;
  isActive: boolean;
}

export const RoleFormDialog: React.FC<RoleFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  editingRole,
}) => {
  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    isAdmin: false,
    displayOrder: '0',
    isActive: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editingRole) {
      setFormData({
        name: editingRole.name,
        isAdmin: editingRole.isAdmin,
        displayOrder: (editingRole.displayOrder ?? 0).toString(),
        isActive: editingRole.isActive,
      });
    } else {
      setFormData({
        name: '',
        isAdmin: false,
        displayOrder: '0',
        isActive: true,
      });
    }
  }, [editingRole, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {editingRole ? 'Edit Organizational Role' : 'Add Organizational Role'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Role Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., VP of Engineering"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="displayOrder">Display Order</Label>
              <Input
                id="displayOrder"
                type="number"
                value={formData.displayOrder}
                onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value })}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">
                Lower numbers appear first in lists
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="isAdmin"
                checked={formData.isAdmin}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, isAdmin: checked as boolean })
                }
              />
              <Label htmlFor="isAdmin" className="cursor-pointer">
                Admin Role
              </Label>
            </div>
            <p className="text-xs text-muted-foreground ml-6">
              Users with this role will have administrator privileges
            </p>

            {editingRole && (
              <>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, isActive: checked as boolean })
                    }
                  />
                  <Label htmlFor="isActive" className="cursor-pointer">
                    Active
                  </Label>
                </div>
                <p className="text-xs text-muted-foreground ml-6">
                  Inactive roles are hidden from user forms
                </p>
              </>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !formData.name}>
              {submitting ? 'Saving...' : editingRole ? 'Update Role' : 'Create Role'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
